import { useId, useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from "recharts";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import { cn } from "../cn";
import { ChartEmptyState } from "./ChartEmptyState";
import { type ChartSegment, chartColorFor } from "./chartTypes";

export interface BreakdownDonutChartProps {
  readonly data: readonly ChartSegment[];
  /** Accessible name for the chart region, e.g. "Dossiertypen". */
  readonly "aria-label": string;
  readonly className?: string;
}

/**
 * Subtle hover lift for a donut segment: a slightly longer outer radius plus
 * a soft shadow in the segment's own colour. Restrained on purpose — this is
 * a report, not a dashboard toy. Recharts feeds this through `activeShape`
 * whenever a tooltip index is active; the same render runs for the base
 * sector, so the fill (palette or muted grey) carries over untouched.
 */
function renderLiftedSector(props: PieSectorDataItem) {
  const outerRadius =
    typeof props.outerRadius === "number" ? props.outerRadius + 2 : undefined;
  return (
    <Sector
      {...props}
      outerRadius={outerRadius}
      style={{
        filter: `drop-shadow(0 1px 3px color-mix(in srgb, ${props.fill} 40%, transparent))`,
      }}
    />
  );
}

/**
 * Hover tooltip for one donut segment: a colour swatch (muted grey for a
 * "Niet vastgelegd" bucket, palette colour otherwise), the label, the raw
 * count and the share of the total. Same visual language as TrendBarChart's
 * tooltip (popover card, xs text, shadow-lg) — the body differs because a
 * segment has three facts, not a value-plus-label pair.
 *
 * Exported so its rendering can be asserted directly: Recharts' own
 * `Tooltip`/`TooltipBoundingBox` mount this through an internal wrapper node
 * whose children only appear reliably in a real browser, not under jsdom's
 * synthetic hover, so a test that hovers a sector and asserts this content
 * in the DOM is not a dependable guard.
 */
export function DonutTooltip({
  active,
  payload,
  data,
  total,
}: {
  readonly active?: boolean;
  readonly payload?: readonly {
    value?: unknown;
    payload?: unknown;
  }[];
  readonly data: readonly ChartSegment[];
  readonly total: number;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const segment = item.payload as ChartSegment | undefined;
  const index = segment ? data.indexOf(segment) : -1;
  if (!segment || index < 0) return null;
  const value = Number(item.value ?? 0);

  return (
    <div className="rounded-lg border border-border bg-popover px-2.5 py-1.5 text-xs shadow-lg">
      <p className="flex items-center gap-1.5 font-medium text-popover-foreground">
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: chartColorFor(segment, index) }}
        />
        {segment.label}
      </p>
      <p className="mt-0.5 text-muted-foreground">
        {value.toLocaleString("nl-NL")} dossiers (
        {Math.round((value / total) * 100)}%)
      </p>
    </div>
  );
}

/**
 * Donut chart with a total in the centre and a share-per-segment legend, for
 * a categorical breakdown (e.g. dossier type, has-images yes/no/unknown).
 * Recharts draws the arcs; the centre total and legend are plain markup so
 * they stay crisp and keep using the design system's text styles.
 *
 * Accessibility: the decorative SVG subtree is `aria-hidden` and taken out of
 * the tab order (`tabIndex={-1}` — Recharts makes the chart wrapper focusable
 * by default, and a focusable element inside an `aria-hidden` subtree is its
 * own defect). The data stays readable because the legend is a real labelled
 * list (label + share per item) and the centre total is repeated as
 * screen-reader-only text on it — not as a `role="img"` region, which is an
 * atomic role and would replace the legend's content with its accessible
 * name.
 */
export function BreakdownDonutChart({
  data,
  "aria-label": ariaLabel,
  className,
}: BreakdownDonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const totalDescriptionId = useId();
  // Hovered segment index, driven by the Pie's own per-sector hover events
  // (not the chart-level ones, which fire on entering the chart as a whole)
  // so the lift follows the pointer across sectors, in step with the
  // tooltip.
  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  if (total === 0) {
    return <ChartEmptyState className={className} />;
  }

  return (
    <div
      className={cn(
        "grid gap-5 sm:grid-cols-[10.5rem_1fr] sm:items-center",
        className,
      )}
    >
      {/*
        The Recharts SVG carries no text and is purely decorative: the legend
        below exposes every label and share as plain text and the total as
        sr-only text, so the chart subtree is hidden from assistive
        technology. aria-hidden goes on our own wrapper div, not the
        ResponsiveContainer — ResponsiveContainer only renders id/className/
        style on its div and drops unknown props.
      */}
      <div aria-hidden="true" className="relative mx-auto size-40">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart tabIndex={-1}>
            <Tooltip
              content={<DonutTooltip data={data} total={total} />}
              // No CSS handles this: the tooltip's mount/unmount is driven by
              // Recharts' own hover state, not an animation, so there is
              // nothing for the reduced-motion media query to shorten.
              isAnimationActive={false}
            />
            <Pie
              data={data as ChartSegment[]}
              dataKey="value"
              nameKey="label"
              innerRadius="65%"
              outerRadius="100%"
              stroke="var(--color-surface)"
              strokeWidth={2}
              isAnimationActive={false}
              // Recharts gives Pie its own default tabIndex (0), separate
              // from the tabIndex on PieChart above: without this, the pie
              // group stays in the tab order despite the wrapper div being
              // aria-hidden.
              rootTabIndex={-1}
              activeIndex={activeIndex}
              activeShape={renderLiftedSector}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
            >
              {data.map((segment, index) => (
                <Cell
                  key={segment.label}
                  fill={chartColorFor(segment, index)}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="text-xl font-semibold tabular-nums">{total}</span>
        </div>
      </div>
      {/* ponytail: max-w-xs keeps each percentage next to its label instead of
        at the far edge of a wide card; widen only if labels truncate often. */}
      <p id={totalDescriptionId} className="sr-only">{`Totaal: ${total}`}</p>
      <ul
        className="max-w-xs space-y-2.5"
        aria-label={ariaLabel}
        aria-describedby={totalDescriptionId}
      >
        {data.map((segment, index) => (
          <li key={segment.label} className="flex items-center gap-2 text-xs">
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: chartColorFor(segment, index) }}
            />
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                segment.muted && "text-muted-foreground",
              )}
            >
              {segment.label}
            </span>
            <span className="font-medium tabular-nums">
              {segment.value.toLocaleString("nl-NL")} (
              {Math.round((segment.value / total) * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
